import { BaseEntity } from '@core/entities/_extra';
import { ApprovedStatus, CourseInstructorType, CourseVisibility, InstructorType, TaskType } from '@core/enums';
import { Expose, plainToClass, Transform, Type } from 'class-transformer';
import { GroupEntity, RecurringDate } from '@core/entities/groups';
import { SafeUrl } from '@angular/platform-browser';
import { UserEntity } from '@core/entities/user.entity';
import { RoomEntity } from '@core/entities/room.entity';
import { InstructorEntity } from '@core/entities';
import { InitializableEntity } from '@core/entities/_extra/intializable.entity';
import { TaskCollaborator } from '@core/entities/task.entity';

export class CourseEntity extends BaseEntity {
  type = 'course';

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  name = '';

  place = '';
  room: string | number = 0;
  visibility: CourseVisibility = CourseVisibility.Public;

  @Expose({ name: 'instructor_type' })
  instructorType: InstructorType | CourseInstructorType = null;

  @Expose({ name: 'limitation_of_participants' })
  limitationOfParticipants: any;

  participants = 'participants';

  @Expose({ name: 'waiting_list' })
  waitingList: any;

  @Expose({ name: 'max_on_waiting_list' })
  maxOnWaitingList: any;

  attachments: any;

  @Expose({ name: 'link_to_ticket_store' })
  linkToTicketStore: any;
  tags: any;
  author: string | number = 0;
  audience: any;

  @Expose({ name: 'allowed_persons' })
  allowedPersons = 0;

  @Expose({ name: 'group_id' })
  groupId: number = null;

  @Expose({ name: 'date_from' })
  dateFrom = '';

  @Expose({ name: 'date_to' })
  dateTo = '';

  @Expose({ name: 'coorganizer' })
  coOrganizer: any;

  @Expose({ name: 'invite_friends' })
  inviteFriends: any;

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  description = '';

  @Expose({ name: 'show_guest_list' })
  @Transform(({ value }) => value === 'true', { toClassOnly: true })
  showGuestList = false;

  chargeable = '0';

  @Expose({ name: 'price_per_participant' })
  pricePerParticipant = '0';

  @Expose({ name: 'create_invoice' })
  createInvoice: any;

  @Expose({ name: 'start_time' })
  startTime = '';

  @Expose({ name: 'end_time' })
  endTime = '';

  @Expose({ name: 'date_repeat' })
  dateRepeat = '';

  recurrence = '';

  @Expose({ name: 'recurring_dates' })
  @Transform(({ value }) => {
    try {
      return value ? plainToClass(RecurringDate, JSON.parse(value)) : null;
    } catch (error) {
      console.error('Failed to parse recurring dates:', error);
      return null;
    }
  })
  recurringDates?: RecurringDate[];

  @Type(() => CourseUser)
  courseUsers: CourseUser[];

  @Expose({ name: 'CourseGroups' })
  courseGroups: any[];

  @Expose({ name: 'CourseInternalInstructor' })
  @Type(() => CourseInstructor)
  courseInternalInstructor: CourseInstructor[];

  @Expose({ name: 'CourseExternalInstructor' })
  @Type(() => CourseInstructor)
  courseExternalInstructor: CourseInstructor[];

  @Expose({ name: 'RoomsDetails' })
  roomsDetails: any[];

  @Type(() => CourseTag)
  courseTags: CourseTag[];

  image = '';
  document = '';

  // For Ui only
  imageUrl: string | SafeUrl;
  currentDate: string;

  // Properties For GET course by id
  roomData: { id: number; name: string };

  @Type(() => UserEntity)
  user: UserEntity;

  @Expose({ name: 'instructorReccurance' })
  @Type(() => RecurringDate)
  instructorRecurrence: RecurringDate[] | string = [];

  @Expose({ name: 'in_instructorReccurance' })
  @Type(() => RecurringDate)
  internalInstructorRecurrence: RecurringDate[] | string = [];

  /**
   * For POST / PUT
   */
  @Expose({ name: 'instructor_external' })
  instructorExternal: any = [];

  @Expose({ name: 'instructor_internal' })
  instructorInternal: any = [];

  @Type(() => RecurringDate)
  courseDate: RecurringDate[] = [];

  isTask = false;

  @Type(() => CourseTask)
  task: CourseTask = new CourseTask();

  @Expose({ name: 'courseReccurance' })
  @Type(() => RecurringDate)
  courseRecurrence: RecurringDate[] = [];

  @Expose({ name: 'course_tags' })
  courseTagsArray: string[] = [];

  @Expose({ name: 'course_users' })
  @Type(() => CoursePostUser)
  courseUsersArray: CoursePostUser[] = [];

  /**
   * For GET
   */
  @Type(() => CourseTask)
  courseTask: CourseTask;

  constructor(init?: Partial<CourseEntity>) {
    super(init);
    this.initEntity(init);
  }
}

export class CourseUser {
  id: number;

  @Expose({ name: 'course_id' })
  courseId: number;

  @Expose({ name: 'user_id' })
  userId: number;

  @Expose({ name: 'approved_status' })
  approvedStatus: number;

  @Expose({ name: 'read_status' })
  readStatus: number;

  @Expose({ name: 'course_updated_status' })
  courseUpdatedStatus: number;

  @Type(() => Users)
  users: Users;
}

export class CoursePostUser {
  @Expose({ name: 'user_id' })
  userId = 0;

  @Expose({ name: 'approved_status' })
  approvedStatus: ApprovedStatus;
}

class Users {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  @Expose({ name: 'member_id' })
  memberId?: number;

  @Expose({ name: 'deleted_user' })
  deletedUser: number;

  get fullName() {
    return `${this.firstname} ${this.lastname}`;
  }
}

class CourseInstructor {
  id: number;

  @Expose({ name: 'course_id' })
  courseId = 0;

  @Expose({ name: 'instructor_id' })
  instructorId = 0;

  @Type(() => CourseInstructorUser)
  externalIns?: CourseInstructorUser;

  @Type(() => CourseInstructorUser)
  internalIns?: CourseInstructorUser;
}

class CourseInstructorUser {
  id: number;

  @Expose({ name: 'first_name' })
  firstName: string;

  @Expose({ name: 'last_name' })
  lastName: string;

  @Expose({ name: 'emaill' })
  email: string;

  @Expose({ name: 'phone_no' })
  phoneNo: string;

  @Expose({ name: 'add_img' })
  addImg: any;
}

class CourseTag {
  @Expose({ name: 'tag_name' })
  tagName: string;
  id: number;

  @Expose({ name: 'course_id' })
  courseId: number;

  @Expose({ name: 'tag_id' })
  tagId: number;
}

export class CoursesCommonEntity {
  @Type(() => UserEntity)
  users: UserEntity[];

  @Type(() => GroupEntity)
  groups: GroupEntity[];

  @Type(() => RoomEntity)
  rooms: RoomEntity[];

  @Type(() => InstructorEntity)
  instructors: InstructorEntity[];
}

export class CourseTask extends InitializableEntity {
  id?: number;

  @Expose()
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  title = '';

  @Expose()
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  description = '';

  @Expose({ name: 'organizer_id' })
  organizerId = 0;

  status = 0;

  @Expose({ name: 'group_id' })
  groupId = 0;

  groups: any = [];

  @Expose({ name: 'user_select' })
  @Type(() => CourseTaskUser)
  userSelect: CourseTaskUser[] = [];

  date = '';

  @Expose({ name: 'type_dropdown' })
  type: TaskType = TaskType.Individual;

  @Type(() => TaskCollaborator)
  taskCollaborators: TaskCollaborator[] = [];

  // For GET/PUT request
  @Expose({ name: 'collaborator' })
  @Type(() => TaskCollaborator)
  collaborators: TaskCollaborator[] = [];

  constructor(values?: Partial<CourseTask>) {
    super();
    this.initEntity(values);
  }
}

export class CourseTaskUser {
  @Expose({ name: 'user_id' })
  userId? = 0;

  id? = 0;

  @Expose({ name: 'user_name' })
  username = '';
}

export class CourseBookingDetails {
  @Expose({ name: 'course_id' })
  courseId: number;

  @Expose()
  @Type(() => CourseEntity)
  course: Partial<CourseEntity>;

  @Expose()
  @Type(() => RecurringDate)
  dates: RecurringDate[];
}
